import { Send } from 'lucide-react';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import {
  PROVIDER_REGION_ORDER,
  REGION_LABELS,
  REQUEST_ACCESS_CONFIG,
  type ProviderEntry,
  type ProviderRegion,
  type RequestAccessFlowConfig,
} from './requestAccessConfig';
import { fireProviderRequestAccessToast } from './providerRequestAccess';
import { type RequestMethod } from './types';

const ProviderCard = ({ provider, method }: { provider: ProviderEntry; method: RequestMethod }) => {
  const { name, Icon, iconBg, iconColor, note, logoUrl } = provider;

  return (
    <button
      type='button'
      onClick={() => fireProviderRequestAccessToast({ providerLabel: name, method })}
      className={cn(
        'tw:text-left tw:group tw:relative tw:flex tw:flex-col tw:gap-2 tw:rounded-xl tw:border tw:bg-white tw:transition-all tw:cursor-pointer',
        'tw:border-[#e5e7eb] tw:hover:border-emerald-400 tw:hover:-translate-y-[1px] tw:p-4',
      )}
    >
      <div
        className={cn(
          'tw:flex tw:items-center tw:justify-center tw:rounded-full tw:w-11 tw:h-11 tw:overflow-hidden',
          !logoUrl && iconBg,
        )}
      >
        {logoUrl ? (
          <img src={logoUrl} alt={`${name} logo`} className='tw:max-h-8 tw:max-w-[80%] tw:object-contain' />
        ) : (
          <Icon className={cn('tw:size-5', iconColor)} />
        )}
      </div>
      <div className='tw:text-base tw:font-bold tw:text-[#1a1d1f] tw:leading-snug'>{name}</div>
      {note && <div className='tw:text-xs tw:text-muted-foreground tw:leading-[1.35]'>{note}</div>}
      <div className='tw:absolute tw:top-2 tw:right-2 tw:flex tw:items-center tw:gap-1 tw:rounded-full tw:bg-emerald-600 tw:text-white tw:px-2 tw:py-0.5 tw:text-[10.5px] tw:font-semibold tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity'>
        <Send className='tw:size-3' />
        Request Access
      </div>
    </button>
  );
};

interface RequestAccessStepProps {
  method: RequestMethod;
}

const renderProviderGrid = (providers: ProviderEntry[], method: RequestMethod) => (
  <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-6 tw:gap-4'>
    {providers.map((provider) => (
      <ProviderCard key={provider.id} provider={provider} method={method} />
    ))}
  </div>
);

const groupProvidersByRegion = (providers: ProviderEntry[]): Map<ProviderRegion, ProviderEntry[]> => {
  const byRegion = new Map<ProviderRegion, ProviderEntry[]>();
  for (const provider of providers) {
    const bucket = byRegion.get(provider.region) ?? [];
    bucket.push(provider);
    byRegion.set(provider.region, bucket);
  }
  return byRegion;
};

const renderProviders = (config: RequestAccessFlowConfig, method: RequestMethod) => {
  if (!config.groupByRegion) {
    return renderProviderGrid(config.providers, method);
  }

  const byRegion = groupProvidersByRegion(config.providers);
  return (
    <div className='tw:space-y-8'>
      {PROVIDER_REGION_ORDER.filter((region) => byRegion.has(region)).map((region) => {
        const providers = byRegion.get(region);
        if (!providers) {
          return null;
        }
        return (
          <div key={region}>
            <p className='tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wider tw:text-muted-foreground tw:mb-4'>
              {REGION_LABELS[region]}
            </p>
            {renderProviderGrid(providers, method)}
          </div>
        );
      })}
    </div>
  );
};

export const RequestAccessStep = ({ method }: RequestAccessStepProps) => {
  const config = REQUEST_ACCESS_CONFIG[method];

  return (
    <Card className='tw:mb-6'>
      <CardContent className='tw:p-6'>
        <h5 className='tw:font-semibold tw:mb-2'>{config.title}</h5>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-6'>
          {config.description} Click any provider to register your interest — we'll let you know when it's live.
        </p>
        {renderProviders(config, method)}
      </CardContent>
    </Card>
  );
};
